/**
 * Shared Creative Gallery card rendering for homepage + gallery page.
 */
(function (global) {
  const Core = () => global.BraceletCore;

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatMoney(n) {
    return `$${Number(n).toFixed(2)}`;
  }

  function formatCount(n) {
    const v = Number(n) || 0;
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
    return String(v);
  }

  function formatPlazaDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}`;
  }

  function wristLabel(cm) {
    const api = Core();
    if (api?.formatWristCmInCompact) return api.formatWristCmInCompact(cm);
    const n = Number(cm) || 0;
    const inches = (n / 2.54).toFixed(2);
    return `${n}cm/${inches}in`;
  }

  function designPrice(design) {
    if (design.price > 0) return design.price;
    const api = Core();
    if (!api) return 0;
    return api.calcTotal(api.expandDesign(design.designJson));
  }

  function detailUrl(base, sep, handle) {
    return `${base}${sep}template=${encodeURIComponent(handle)}`;
  }

  function buildCard(design, opts = {}) {
    const handle = design.handle || design.id;
    const link = document.createElement('a');
    link.className = 'creative-plaza__card design-inspiration__card';
    link.href = detailUrl(opts.detailBase, opts.detailSep, handle);
    link.setAttribute('role', 'listitem');

    const imageWrap = document.createElement('div');
    imageWrap.className = 'design-inspiration__card-image design-inspiration__card-image--plaza';

    const canvas = document.createElement('canvas');
    canvas.className = 'design-inspiration__preview-canvas';
    canvas.width = 280;
    canvas.height = 280;
    imageWrap.appendChild(canvas);

    const overlay = document.createElement('span');
    overlay.className = 'design-inspiration__card-overlay';
    overlay.textContent = opts.viewLabel || 'View design';
    imageWrap.appendChild(overlay);

    const meta = document.createElement('div');
    meta.className = 'design-inspiration__card-meta creative-plaza__card-meta';

    const titleText = design.title || handle;
    const author = opts.authorLabel || opts.communityLabel || 'Community';
    const dateStr = formatPlazaDate(design.createdAt || design.updatedAt);
    const subLine = dateStr ? `${author} · ${dateStr}` : author;
    const price = formatMoney(designPrice(design));
    const wrist = wristLabel(design.wristSizeCm ?? 15);
    const views = formatCount(design.viewCount ?? design.sort ?? 0);

    meta.innerHTML = `
      <p class="design-inspiration__card-title" title="${escapeHtml(titleText)}">${escapeHtml(titleText)}</p>
      <p class="creative-plaza__card-sub">${escapeHtml(subLine)}</p>
      <div class="creative-plaza__card-footer">
        <div class="creative-plaza__card-facts">
          <span class="design-inspiration__card-price">${escapeHtml(price)}</span>
          <span class="creative-plaza__card-sep" aria-hidden="true">·</span>
          <span class="creative-plaza__card-size">${escapeHtml(wrist)}</span>
        </div>
        <div class="creative-plaza__card-stats" aria-label="${escapeHtml(opts.viewsLabel || 'Views')}">
          <span class="creative-plaza__stat" title="${escapeHtml(opts.viewsLabel || 'Views')}">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            <span>${escapeHtml(views)}</span>
          </span>
        </div>
      </div>
    `;

    link.append(imageWrap, meta);
    return link;
  }

  function wristFromDesign(design) {
    const api = Core();
    const normalized = api?.normalizeDesignJson?.(design?.designJson);
    return normalized?.wrist_size_cm ?? 15;
  }

  async function drawCardThumbnails(cards, designs) {
    const core = Core();
    if (!core) return;
    await Promise.all(
      cards.map((card, i) => {
        const canvas = card.querySelector('canvas');
        const beads = core.expandDesign(designs[i].designJson);
        return core.drawThumbnail(canvas, beads, null, {
          galleryCard: true,
          wristCm: wristFromDesign(designs[i]),
        });
      })
    );
  }

  async function renderCards(container, designs, opts = {}) {
    if (!container) return;
    container.replaceChildren();
    const core = Core();
    if (!core) return;

    const cards = [];
    for (let i = 0; i < designs.length; i++) {
      const card = buildCard(designs[i], { ...opts, toneIndex: i });
      container.appendChild(card);
      cards.push(card);
    }
    await drawCardThumbnails(cards, designs);
  }

  async function appendCards(container, designs, opts = {}, startIndex = 0) {
    if (!container || !designs.length) return;
    const core = Core();
    if (!core) return;

    const cards = [];
    for (let i = 0; i < designs.length; i++) {
      const card = buildCard(designs[i], { ...opts, toneIndex: startIndex + i });
      container.appendChild(card);
      cards.push(card);
    }
    await drawCardThumbnails(cards, designs);
  }

  global.PlazaRender = {
    buildCard,
    renderCards,
    appendCards,
    escapeHtml,
    formatMoney,
    formatCount,
    detailUrl,
  };
})(typeof window !== 'undefined' ? window : globalThis);
